import { memo } from "react"

import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from "@xyflow/react"
import { useTranslation } from "react-i18next"

import type { HostedAgentLinkEdge as HostedAgentLinkEdgeModel } from "./hosted-agent-map-utils"
import type { EdgeProps } from "@xyflow/react"

import { Key, UserRound } from "@/components/icons"
import { cn } from "@/lib/utils"

/**
 * Directed AgentLink edge. The arrowhead (`markerEnd`, set by the canvas'
 * defaultEdgeOptions) shows from→to. Disabled links are dashed; the label chip
 * shows the edge identity — `caller` (end-user permissions, B5 default) or
 * `agent` (permission bridge, highlighted with a key so it is never missed).
 */
function HostedAgentLinkEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  data,
  selected,
}: EdgeProps<HostedAgentLinkEdgeModel>) {
  const { t } = useTranslation()
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })
  const link = data?.link
  const enabled = link?.enabled ?? false
  const isBridge = link?.identity === "agent"
  const strokeColor = enabled
    ? "hsl(var(--foreground) / 0.55)"
    : "hsl(var(--muted-foreground) / 0.6)"

  return (
    <>
      <BaseEdge
        id={id}
        markerEnd={markerEnd}
        path={edgePath}
        style={{
          strokeWidth: selected ? 3 : 2,
          strokeDasharray: enabled ? undefined : "6 4",
          stroke: selected ? "hsl(var(--primary))" : strokeColor,
        }}
      />
      {/* Wide transparent hit path so the edge is easy to click. */}
      <path
        className="react-flow__edge-interaction"
        d={edgePath}
        fill="none"
        stroke="transparent"
      />
      <EdgeLabelRenderer>
        <div
          style={{ transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)` }}
          className={cn(
            "nodrag nopan pointer-events-auto absolute flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-semibold",
            isBridge
              ? "border-foreground bg-warning text-warning-foreground"
              : "bg-background text-muted-foreground",
            !enabled && "opacity-70",
            selected && "border-primary"
          )}
        >
          {isBridge ? (
            <Key aria-hidden="true" className="size-3" />
          ) : (
            <UserRound aria-hidden="true" className="size-3" />
          )}
          {isBridge
            ? t("adminDashboard.hostedAgents.map.identityAgentShort", { defaultValue: "agent" })
            : t("adminDashboard.hostedAgents.map.identityCallerShort", { defaultValue: "caller" })}
          {!enabled ? (
            <span className="font-normal">
              · {t("adminDashboard.hostedAgents.map.linkOff", { defaultValue: "off" })}
            </span>
          ) : null}
        </div>
      </EdgeLabelRenderer>
    </>
  )
}

export const HostedAgentLinkEdge = memo(HostedAgentLinkEdgeComponent)
